// Phase 3D.5-B - Pure, deterministic SIMULATED CAMPAIGN OUTCOMES engine for the
// Savoie pilot. It transforms the CANONICAL output of
// buildSimulatedCampaign(recommendationResult) (see savoieCampaignSimulation.js)
// into a SEPARATE object representing strictly SIMULATED, NON-OBSERVED and
// NON-PREDICTIVE campaign scenario outcomes.
//
// SCOPE / SAFETY CONTRACT:
//   * PURE, SYNCHRONOUS, DETERMINISTIC. No side effects.
//   * NO network / NO HTTP / NO fetch / NO axios / NO DNS, NO DB, NO storage,
//     NO AI/provider, NO ad platform, NO secret.
//   * NO Date / NO Date.now / NO performance.now, NO randomness,
//     NO environment read.
//   * NO import with side effects (this module imports nothing).
//   * Does NOT render, does NOT create a page/route/component/backend.
//   * Does NOT create, publish or activate a real campaign.
//   * Never mutates its input (campaignResult / nested objects).
//   * Never fabricates a silent fallback or a partial candidate.
//   * Fail-closed: any contract violation yields an explicit { status:"none" }.
//   * The 3D.4 contract is NOT modified: expected_results stays null upstream
//     and this engine REQUIRES campaignResult.expected_results === null.
//
// SEPARATION OF EVIDENCE vs ASSUMPTIONS vs SCENARIO_OUTCOMES:
//   * EVIDENCE comes EXCLUSIVELY from campaignResult.evidence_reference
//     (synthetic historical figures inherited from the recommendation).
//   * ASSUMPTIONS are the fictional scenario hypotheses (budget + synthetic
//     CPM / frequency / CTR / conversion-rate constants).
//   * SCENARIO_OUTCOMES are the simulated mathematical outputs. They are NOT
//     observed, NOT measured and NOT predicted.

// --- Frozen synthetic demo constants. -------------------------------------
// These values are FICTIONAL, ARBITRARY, NOT sourced from any ad platform,
// NOT sourced from client data, NOT derived from any real observation, NOT
// predictive, and reserved for the synthetic pilot demonstration only.
export const SYNTHETIC_CPM_EUR = 8; // fictional cost-per-mille (EUR), demo only
export const SYNTHETIC_AVG_FREQUENCY = 2.5; // fictional average frequency, demo only
export const SYNTHETIC_CTR = 0.009; // fictional click-through rate, demo only
export const SYNTHETIC_CONVERSION_RATE = 0.02; // fictional conversion rate, demo only

export const SCENARIO_CONSTANTS = Object.freeze({
  synthetic_cpm_eur: SYNTHETIC_CPM_EUR,
  synthetic_avg_frequency: SYNTHETIC_AVG_FREQUENCY,
  synthetic_ctr: SYNTHETIC_CTR,
  synthetic_conversion_rate: SYNTHETIC_CONVERSION_RATE,
});

// --- Exact disclaimer (verbatim, required, single JS string). -------------
export const SIMULATED_OUTCOMES_DISCLAIMER =
  "Résultats de scénario entièrement simulés à partir d'une campagne fictive " +
  "et de constantes synthétiques de démonstration. Ces valeurs ne sont ni " +
  "observées, ni mesurées, ni prédites ; elles ne constituent pas une " +
  "prévision, une probabilité, un objectif ni un ROI. Aucune campagne réelle " +
  "n'est créée, publiée ou activée.";

// Deterministic reason tokens for the explicit "none" result. Order mirrors the
// documented gate order; the FIRST invalid gate determines the reason.
export const OUTCOMES_NONE_REASONS = Object.freeze({
  INVALID_INPUT: "invalid_input",
  NOT_SIMULATED: "not_simulated",
  INVALID_SIMULATION_FLAG: "invalid_simulation_flag",
  ACTIVATION_NOT_DISABLED: "activation_not_disabled",
  PUBLISH_NOT_DISABLED: "publish_not_disabled",
  EXTERNAL_ID_NOT_NULL: "external_id_not_null",
  EXPECTED_RESULTS_NOT_NULL: "expected_results_not_null",
  INVALID_PROVENANCE: "invalid_provenance",
  INVALID_META: "invalid_meta",
  MISSING_CAMPAIGN_ID: "missing_campaign_id",
  MISSING_RECOMMENDATION_ID: "missing_recommendation_id",
  MISSING_OPPORTUNITY_ID: "missing_opportunity_id",
  INVALID_AUDIENCE: "invalid_audience",
  INVALID_PERIOD: "invalid_period",
  INVALID_BUDGET: "invalid_budget",
  NON_EUR_CURRENCY: "non_eur_currency",
  INVALID_CHANNELS: "invalid_channels",
  INVALID_OBJECTIVES: "invalid_objectives",
  INVALID_EVIDENCE: "invalid_evidence",
  MISSING_DISCLAIMER: "missing_disclaimer",
});

// --- Pure validators ------------------------------------------------------
const _isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const _isNonEmptyStr = (v) => typeof v === "string" && v.length > 0;
const _isFiniteNum = (v) => typeof v === "number" && Number.isFinite(v);

// "YYYY-MM" with month 01..12 -> true ; otherwise false.
function _isValidPeriod(str) {
  if (typeof str !== "string") return false;
  const m = /^(\d{4})-(\d{2})$/.exec(str);
  if (!m) return false;
  const month = Number(m[2]);
  return month >= 1 && month <= 12;
}

// Meta must mirror the SIMULATED campaign demo contract (no transformation).
function _metaValid(meta) {
  if (!_isObj(meta)) return false;
  if (meta.is_demo !== true) return false;
  if (meta.provenance !== "synthetic_demo") return false;
  if (meta.provenance_label !== "DONNÉES DE DÉMONSTRATION") return false;
  if (meta.affiliation !== "none") return false;
  if (meta.simulation !== true) return false;
  if (meta.simulation_label !== "SIMULATION") return false;
  return true;
}

// Fail-closed result. Meta is preserved ONLY if safe and conform, else null.
function _none(reason, meta) {
  return {
    status: "none",
    scenario_outcomes: null,
    scenario_outcomes_display: null,
    reason,
    meta: _metaValid(meta) ? { ...meta } : null,
    simulation: true,
    observed: false,
    predictive: false,
    not_a_forecast: true,
    activation_status: "disabled",
    publish_status: "disabled",
    external_campaign_id: null,
  };
}

// Small helper to build a transparent assumption entry (fresh object).
function _assumption(value) {
  return {
    value,
    assumption: true,
    source: "synthetic_demo_scenario",
    real_data: false,
    predictive: false,
  };
}

/**
 * Build a SEPARATE, strictly SIMULATED, non-observed, non-predictive outcomes
 * object from the canonical output of buildSimulatedCampaign(...).
 * @param {object} campaignResult - canonical simulated campaign result.
 * @returns scenario outcomes object OR explicit { status:"none", ... }.
 */
export function buildSimulatedCampaignOutcomes(campaignResult) {
  const R = OUTCOMES_NONE_REASONS;

  // 1. Basic shape.
  if (!_isObj(campaignResult)) return _none(R.INVALID_INPUT, null);

  const c = campaignResult;
  const meta = c.meta;

  // 2-7. Canonical safety flags (fail-closed, no coercion).
  if (c.status !== "simulated") return _none(R.NOT_SIMULATED, meta);
  if (c.simulation !== true) return _none(R.INVALID_SIMULATION_FLAG, meta);
  if (c.activation_status !== "disabled") return _none(R.ACTIVATION_NOT_DISABLED, meta);
  if (c.publish_status !== "disabled") return _none(R.PUBLISH_NOT_DISABLED, meta);
  if (c.external_campaign_id !== null) return _none(R.EXTERNAL_ID_NOT_NULL, meta);
  if (c.expected_results !== null) return _none(R.EXPECTED_RESULTS_NOT_NULL, meta);

  // 8-9. Provenance + demo meta contract.
  if (c.provenance !== "synthetic_demo") return _none(R.INVALID_PROVENANCE, meta);
  if (!_metaValid(meta)) return _none(R.INVALID_META, meta);

  // 10-12. Identity fields.
  if (!_isNonEmptyStr(c.id)) return _none(R.MISSING_CAMPAIGN_ID, meta);
  if (!_isNonEmptyStr(c.based_on_recommendation_id)) {
    return _none(R.MISSING_RECOMMENDATION_ID, meta);
  }
  if (!_isNonEmptyStr(c.based_on_opportunity_id)) {
    return _none(R.MISSING_OPPORTUNITY_ID, meta);
  }

  // 13-14. Audience carried by the campaign (never hardcoded here).
  const aud = c.audience;
  if (
    !_isObj(aud) ||
    !_isNonEmptyStr(aud.source_market_code) ||
    !_isNonEmptyStr(aud.source_market_name)
  ) {
    return _none(R.INVALID_AUDIENCE, meta);
  }

  // 15-16. Period.
  const period = c.period;
  if (!_isObj(period)) return _none(R.INVALID_PERIOD, meta);
  if (!_isValidPeriod(period.month)) return _none(R.INVALID_PERIOD, meta);
  if (period.type !== "synthetic_demo_period") return _none(R.INVALID_PERIOD, meta);

  // 17-19. Budget (finite > 0), EUR currency, and demo assumption flag.
  const budget = c.budget;
  if (!_isObj(budget)) return _none(R.INVALID_BUDGET, meta);
  if (!_isFiniteNum(budget.amount) || budget.amount <= 0) {
    return _none(R.INVALID_BUDGET, meta);
  }
  if (budget.currency !== "EUR") return _none(R.NON_EUR_CURRENCY, meta);
  if ("assumption" in budget && budget.assumption !== true) {
    return _none(R.INVALID_BUDGET, meta);
  }

  // 20-21. Channels: non-empty, each explicitly synthetic assumption.
  const channels = c.channels;
  if (!Array.isArray(channels) || channels.length === 0) {
    return _none(R.INVALID_CHANNELS, meta);
  }
  for (const ch of channels) {
    if (!_isObj(ch) || ch.assumption !== true) {
      return _none(R.INVALID_CHANNELS, meta);
    }
  }

  // 22. Objectives: non-empty.
  const objectives = c.objectives;
  if (!Array.isArray(objectives) || objectives.length === 0) {
    return _none(R.INVALID_OBJECTIVES, meta);
  }

  // 23-27. Evidence reference (synthetic historical figures only).
  const ev = c.evidence_reference;
  if (!_isObj(ev)) return _none(R.INVALID_EVIDENCE, meta);
  if (!_isValidPeriod(ev.evidence_period) || !_isValidPeriod(ev.comparison_period)) {
    return _none(R.INVALID_EVIDENCE, meta);
  }
  if (!_isFiniteNum(ev.growth_percent)) return _none(R.INVALID_EVIDENCE, meta);
  if (!_isFiniteNum(ev.visitor_arrivals) || ev.visitor_arrivals < 0) {
    return _none(R.INVALID_EVIDENCE, meta);
  }
  if (!_isFiniteNum(ev.market_share_percent) || ev.market_share_percent < 0) {
    return _none(R.INVALID_EVIDENCE, meta);
  }

  // 28. Disclaimer coherence on the source campaign.
  if (!_isNonEmptyStr(c.disclaimer)) return _none(R.MISSING_DISCLAIMER, meta);

  // --- Deterministic scenario mathematics (no rounding here) ------------
  // rawClicks / rawConversions are the exact equivalents of
  //   rawImpressions * SYNTHETIC_CTR   and   rawClicks * SYNTHETIC_CONVERSION_RATE
  // reformulated over the constants' decimal scale to avoid IEEE-754 binary
  // dust (e.g. 1500000 * 0.009 = 13499.999999999998). SYNTHETIC_CTR * 1000 and
  // SYNTHETIC_CONVERSION_RATE * 100 are exact integers (9 and 2), so no value is
  // rounded or hardcoded: the numbers still derive solely from the constants.
  const rawImpressions = (budget.amount / SYNTHETIC_CPM_EUR) * 1000;
  const rawReach = rawImpressions / SYNTHETIC_AVG_FREQUENCY;
  const rawClicks = (rawImpressions * (SYNTHETIC_CTR * 1000)) / 1000;
  const rawConversions =
    (rawClicks * (SYNTHETIC_CONVERSION_RATE * 100)) / 100;

  // --- EVIDENCE (faithful copy, never assumptions) ----------------------
  const evidence = {
    evidence_period: ev.evidence_period,
    comparison_period: ev.comparison_period,
    growth_percent: ev.growth_percent,
    visitor_arrivals: ev.visitor_arrivals,
    market_share_percent: ev.market_share_percent,
  };

  // --- ASSUMPTIONS (fictional scenario hypotheses) ----------------------
  const assumptions = {
    budget: {
      amount: budget.amount,
      currency: "EUR",
      assumption: true,
      source: "synthetic_demo_scenario",
      real_data: false,
      predictive: false,
    },
    synthetic_cpm_eur: _assumption(SYNTHETIC_CPM_EUR),
    synthetic_avg_frequency: _assumption(SYNTHETIC_AVG_FREQUENCY),
    synthetic_ctr: _assumption(SYNTHETIC_CTR),
    synthetic_conversion_rate: _assumption(SYNTHETIC_CONVERSION_RATE),
  };

  // --- SCENARIO_OUTCOMES (raw) + display rounding ONLY ------------------
  const scenario_outcomes = {
    scenario_impressions: rawImpressions,
    scenario_reach: rawReach,
    scenario_clicks: rawClicks,
    scenario_conversions: rawConversions,
  };
  const scenario_outcomes_display = {
    scenario_impressions: Math.round(rawImpressions),
    scenario_reach: Math.round(rawReach),
    scenario_clicks: Math.round(rawClicks),
    scenario_conversions: Math.round(rawConversions),
  };

  // Campaign meta: NON-MUTATING copy with simulation flags asserted.
  const outcomesMeta = {
    ...meta,
    simulation: true,
    simulation_label: "SIMULATION",
  };

  const userFacingText =
    `Résultats de scénario simulés pour le marché ${aud.source_market_name} ` +
    `(${aud.source_market_code}) sur la période ${period.month}. Ces valeurs ` +
    `sont calculées à partir d'hypothèses synthétiques : elles ne sont ni ` +
    `observées ni prédites et ne constituent ni une prévision ni un objectif.`;

  return {
    status: "scenario",
    id: `campaign-outcomes:${c.id}`,
    based_on_campaign_id: c.id,
    based_on_recommendation_id: c.based_on_recommendation_id,
    based_on_opportunity_id: c.based_on_opportunity_id,
    provenance: "synthetic_demo",
    simulation: true,
    observed: false,
    predictive: false,
    not_a_forecast: true,
    activation_status: "disabled",
    publish_status: "disabled",
    external_campaign_id: null,
    evidence,
    assumptions,
    scenario_constants: {
      synthetic_cpm_eur: SYNTHETIC_CPM_EUR,
      synthetic_avg_frequency: SYNTHETIC_AVG_FREQUENCY,
      synthetic_ctr: SYNTHETIC_CTR,
      synthetic_conversion_rate: SYNTHETIC_CONVERSION_RATE,
    },
    scenario_outcomes,
    scenario_outcomes_display,
    meta: outcomesMeta,
    disclaimer: SIMULATED_OUTCOMES_DISCLAIMER,
    user_facing_text: userFacingText,
  };
}

export const _outcomesInternals = {
  _isValidPeriod,
  _metaValid,
};
